import * as palette from "@govlaunch/palette";
import React, { createRef, useState } from "react";
import Downshift from "downshift";
import { HitsProvided } from "react-instantsearch-core";
import { Configure, connectHits, connectSearchBox, InstantSearch } from "react-instantsearch-dom";
import { useIntl } from "react-intl";
import algoliaClient from "~/components/search/utils/algoliaClient";
import getIndexName from "~/components/search/utils/getIndexName";
import { Margin } from "~/components/spacings";
import { ITag, ITagType } from "~/types/types";

type AlgoliaTag = Pick<ITag, "name" | "_id"> & { objectID?: string };

interface ITagsSelectProps extends Omit<HitsProvided<ITag>, "hits"> {
  hits: (AlgoliaTag & { objectID: string })[];
  placeholder?: string;
  value: AlgoliaTag[];
  type?: ITagType | ITagType[];
  onChange: (selectedTag: any) => any;
  onFocus: (event?: React.FocusEvent<HTMLElement> | undefined) => void;
  onBlur: (event?: React.FocusEvent<HTMLElement> | undefined) => void;
}

function parseFacetFilters(type: ITagType | ITagType[] | null | undefined) {
  if (!type) {
    return [[]];
  }

  let includedTypes: string[] | null = null;

  if (!Array.isArray(type)) {
    includedTypes = [`types:${type}`];
  }

  if (Array.isArray(type) && type.length > 0) {
    includedTypes = [...type.map((value) => `types:${value}`)];
  }

  return [includedTypes];
}

const TagsSelectContainer = (props: Omit<ITagsSelectProps, "hits">) => {
  return (
    <InstantSearch indexName={getIndexName("tags")} searchClient={algoliaClient}>
      <ConnectedTagsSelect {...props} />
    </InstantSearch>
  );
};

const TagsSelect: React.StatelessComponent<ITagsSelectProps> = ({
  hits: tags,
  placeholder,
  value,
  onFocus,
  onBlur,
  type,
  onChange,
}) => {
  const intl = useIntl();
  const inputRef = createRef<HTMLInputElement>();
  const [inputValue, setInputValue] = useState("");
  const [isFocused, setIsFocused] = useState(false);
  const normalizedValue = Array.isArray(value) ? value : [];
  const selectableTags = skipTags(normalizedValue, tags).slice(0, 5);

  return (
    <>
      <SearchBox defaultRefinement={inputValue} />
      {type && <Configure facetFilters={parseFacetFilters(type)} />}

      <Downshift<AlgoliaTag>
        id="tags-input-memo"
        selectedItem={null}
        inputValue={inputValue}
        itemToString={(item) => item?._id || ""}
        stateReducer={(_, changes) => {
          switch (changes.type) {
            case Downshift.stateChangeTypes.changeInput:
              return {
                ...changes,
                highlightedIndex: null,
              };
            case Downshift.stateChangeTypes.keyDownEnter:
              return {
                ...changes,
                isOpen: true,
                highlightedIndex: 0,
              };
            case Downshift.stateChangeTypes.clickItem:
              return {
                ...changes,
                isOpen: true,
                highlightedIndex: 0,
              };
          }

          return changes;
        }}
        onChange={(selectedTag) => {
          setInputValue("");
          onChange([...value, selectedTag]);
        }}
        onInputValueChange={(value, changes) => {
          if ((changes as any).type === Downshift.stateChangeTypes.changeInput) {
            setInputValue(value);
          }
        }}
      >
        {({ getInputProps, getItemProps, getMenuProps, highlightedIndex, isOpen, openMenu }) => (
          <div>
            <div
              onClick={() => {
                if (inputRef.current) {
                  (inputRef.current as HTMLInputElement).focus();
                }
              }}
              css={{
                display: "flex",
                alignItems: "center",
                minHeight: 42,
                padding: 5,
                border: `solid 1px ${isFocused ? palette.primary : palette.lightestGray}`,
                borderRadius: 5,
                cursor: "text",
              }}
              data-intercom-target="Project Tags Field"
            >
              <div
                css={{
                  display: "flex",
                  flexWrap: "wrap",
                  alignItems: "center",
                  width: "100%",
                }}
              >
                {normalizedValue.map((tag) => {
                  return <Tag key={tag.objectID || tag._id}>{tag.name}</Tag>;
                })}

                <input
                  {...getInputProps({
                    ref: inputRef,
                    onFocus: (event) => {
                      setIsFocused(true);

                      if (onFocus) {
                        onFocus(event);
                      }

                      openMenu();
                    },
                    onBlur: (event) => {
                      setIsFocused(false);

                      if (onBlur) {
                        onBlur(event);
                      }
                    },
                    onKeyDown: (event) => {
                      if (event.key === "Backspace" && !(event.target as HTMLInputElement).value) {
                        onChange([...value.slice(0, -1)]);
                      } else if (event.key === "Enter") {
                        event.preventDefault();
                      }
                    },
                  })}
                  placeholder={
                    placeholder || intl.formatMessage({ defaultMessage: "Type to select tags...", id: "af49E1" })
                  }
                  css={{
                    flexGrow: 1,
                    margin: 5,
                    fontSize: 14,
                    border: 0,
                    outline: 0,
                    "&::placeholder": {
                      color: palette.sealBlue,
                      fontSize: 14,
                    },
                  }}
                />
              </div>
            </div>

            {isOpen && selectableTags.length > 0 && (
              <Margin mt={10}>
                <div
                  {...getMenuProps()}
                  css={{
                    display: "flex",
                    flexWrap: "wrap",
                    marginLeft: -5,
                  }}
                >
                  {selectableTags.map((tag, index) => {
                    return (
                      <SelectableTag
                        {...getItemProps({
                          item: tag,
                          index,
                        })}
                        css={{
                          backgroundColor: highlightedIndex === index ? palette.lightBlue : null,
                          cursor: "pointer",
                          "&:hover": {
                            backgroundColor: palette.lightestBlue,
                          },
                        }}
                      >
                        {tag.name}
                      </SelectableTag>
                    );
                  })}
                </div>
              </Margin>
            )}
          </div>
        )}
      </Downshift>
    </>
  );
};

const SelectableTag: React.FunctionComponent = (props) => {
  return (
    <span
      {...props}
      css={{
        margin: 5,
        padding: "5px 10px",
        color: palette.darkGray,
        fontSize: 12,
        backgroundColor: palette.lightestGray,
        borderRadius: 10,
      }}
    />
  );
};

const Tag: React.FunctionComponent = (props) => {
  return (
    <span
      {...props}
      css={{
        margin: 5,
        padding: "5px 5px",
        color: palette.darkGray,
        fontSize: 12,
        backgroundColor: "rgba(219, 236, 247, 0.5)",
        borderRadius: 10,
      }}
    />
  );
};

function skipTags(tagsToSkip: AlgoliaTag[], tags: AlgoliaTag[]): AlgoliaTag[] {
  const idsToSkip = tagsToSkip.map(({ objectID, _id }) => objectID || _id);

  return tags.filter((tag) => {
    return !idsToSkip.includes(tag.objectID || tag._id);
  });
}

const SearchBox = connectSearchBox(() => null);

const ConnectedTagsSelect = connectHits(TagsSelect as any);

export default TagsSelectContainer;
